import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import WebsiteLayout from '../website/components/WebsiteLayout';
import Reveal from '../website/components/Reveal';
import SubstrateViz from '../website/components/SubstrateViz';
import { useLanguage } from '../../hooks/useLanguage';

const HERO_CO_CREATION_VIDEO = 'https://thoughtfuloasis-public.s3.us-east-1.amazonaws.com/missionos-vnog-cocreation.mp4';
const CASE_VMO_DETAIL_VIDEO = 'https://thoughtfuloasis-public.s3.us-east-1.amazonaws.com/vmo-detail.mp4';

const NLLanding: React.FC = () => {
  const [language] = useLanguage();
  const en = language === 'en';

  return (
    <WebsiteLayout>
      <Helmet>
        <title>Thoughtful Oasis — AI can be more than a black box.</title>
        <meta
          name="description"
          content="An AI research lab building infrastructure for trustworthy applications in mission-critical industries, like firefighting and government transparency. In production with VNOG and the Dutch Ministry of Justice and Security."
        />
        <link rel="canonical" href="https://thoughtful-oasis.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Thoughtful Oasis" />
        <meta property="og:title" content="Thoughtful Oasis — AI can be more than a black box." />
        <meta
          property="og:description"
          content="An AI research lab building infrastructure for trustworthy applications in mission-critical industries, like firefighting and government transparency. In production with VNOG and the Dutch Ministry of Justice and Security."
        />
        <meta property="og:url" content="https://thoughtful-oasis.com/" />
        <meta property="og:image" content="https://thoughtful-oasis.com/assets/website/photos/og-home.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content={en ? 'en_GB' : 'nl_NL'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Thoughtful Oasis — AI can be more than a black box." />
        <meta
          name="twitter:description"
          content="An AI research lab building infrastructure for trustworthy applications in mission-critical industries, like firefighting and government transparency. In production with VNOG and the Dutch Ministry of Justice and Security."
        />
        <meta name="twitter:image" content="https://thoughtful-oasis.com/assets/website/photos/og-home.jpg" />
        <style>{`
          .hero--split { position: relative; }
          .hero--split .hero__inner { position: relative; z-index: 2; max-width: var(--max-w); width: 100%; }
          .hero--split .hero__inner > h1,
          .hero--split .hero__inner > .hero__sub,
          .hero--split .hero__inner > .hero__meta,
          .hero--split .hero__inner > .hero__cta { max-width: var(--max-w-prose); }
          .hero--split h1, .hero--split .hero__sub, .hero--split .hero__meta {
            text-shadow: 0 0 24px var(--cream), 0 0 12px var(--cream);
          }
          .hero__viz {
            position: absolute; top: 50%;
            right: max(var(--pad-x), calc((100% - var(--max-w)) / 2 + var(--pad-x) - 84px));
            transform: translateY(-50%);
            width: 52%; max-width: 608px; aspect-ratio: 1 / 1; z-index: 1; pointer-events: none;
            --line: rgba(15, 18, 16, 0.10); --line-2: rgba(15, 18, 16, 0.18);
            --accent: var(--teal); --ink-2: #2F3530;
          }
          .hero__viz .axis-label, .hero__viz .entity-label, .hero__viz .entity-tag {
            font-family: var(--f-mono); fill: var(--ink); paint-order: stroke;
            stroke: var(--cream); stroke-width: 2.5; stroke-linejoin: round;
          }
          .hero__viz .axis-label   { font-size: 9px;   font-weight: 500; fill: #2F3530; }
          .hero__viz .entity-label { font-size: 8.5px; font-weight: 500; fill: var(--ink); }
          .hero__viz .entity-tag   { font-size: 7.5px; fill: var(--ink-soft); }
          @media (max-width: 900px) {
            .hero--split { flex-direction: column; align-items: stretch; justify-content: flex-start; padding-top: 56px; }
            .hero__viz { position: relative; top: auto; right: auto; transform: none;
              width: 100%; max-width: none; max-height: 360px; margin-top: 32px; opacity: 0.6; }
            .hero--split h1, .hero--split .hero__sub, .hero--split .hero__meta { text-shadow: none; }
          }
        `}</style>
        <script type="application/ld+json">{`{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Thoughtful Oasis",
          "legalName": "Thoughtful Oasis BV",
          "url": "https://thoughtful-oasis.com/",
          "logo": "https://thoughtful-oasis.com/assets/website/brand/wordmark.svg",
          "foundingDate": "2025",
          "founders": [
            {"@type": "Person", "name": "Kaspar Rothenfusser"},
            {"@type": "Person", "name": "Bekk Blando"},
            {"@type": "Person", "name": "Rens Dommerholt"}
          ],
          "address": {"@type": "PostalAddress", "addressLocality": "Enschede", "addressCountry": "NL"},
          "sameAs": ["https://www.linkedin.com/company/oasis-search/"],
          "description": "AI research lab building infrastructure for trustworthy applications in mission-critical industries, like firefighting and government transparency."
        }`}</script>
      </Helmet>

      {/* §1 — Hero (split-layout — substrate viz right) */}
      <header className="hero hero--split">
        <div className="hero__inner">
          <h1>
            {en ? (
              <>AI can be more<br />than a black box.</>
            ) : (
              <>AI kan meer zijn<br />dan een black box.</>
            )}
          </h1>
          <p className="hero__sub">
            {en ? (
              <>
                We build AI systems that are interpretable by design,<br />
                combining the power of machine learning<br />
                with the depth of human expertise.
              </>
            ) : (
              <>
                Wij bouwen AI-systemen die interpreteerbaar zijn van opzet,<br />
                de kracht van machine learning gecombineerd<br />
                met de diepte van menselijke expertise.
              </>
            )}
          </p>
          <p className="hero__meta">
            {en ? (
              <>
                It turns out that systems that think like us are possible — and very capable.<br />
                In production with VNOG and the Dutch Ministry of Justice and Security.
              </>
            ) : (
              <>
                Het blijkt dat systemen die denken zoals wij mogelijk zijn — en zeer capabel.<br />
                In productie bij VNOG en het Ministerie van Justitie en Veiligheid.
              </>
            )}
          </p>
          <div className="hero__cta">
            <Link className="btn btn-primary btn-primary--green" to="/research">
              {en ? 'Read the research' : 'Lees het onderzoek'}
            </Link>
            <Link className="btn btn-secondary" to="/applications">
              {en ? 'See the applications' : 'Bekijk de toepassingen'}
            </Link>
          </div>
        </div>
        <SubstrateViz id="hero-viz" className="hero__viz" />
      </header>

      <div className="divider"></div>

      {/* §2 — The shift */}
      <section className="section">
        <div className="container">
          <Reveal className="section-head">
            <span className="label">{en ? 'The shift' : 'De omslag'}</span>
            <h2 style={{ marginTop: 18 }}>
              {en
                ? "Today's AI hides its reasoning in cryptic math and numbers."
                : 'De AI van vandaag verbergt zijn redenering in cryptische wiskunde en getallen.'}
            </h2>
            <p className="lede" style={{ marginTop: 28 }}>
              {en
                ? 'Modern AI runs on embeddings, vectors of thousands of unnamed dimensions. The system encodes, reasons, and decodes inside that hidden space. A black box by construction.'
                : 'Moderne AI draait op embeddings, vectoren van duizenden niet-benoemde dimensies. Het systeem codeert, redeneert en decodeert binnen die verborgen ruimte. Een black box van opzet.'}
            </p>
            <p className="lede" style={{ marginTop: 16 }}>
              {en ? (
                <>
                  We rethought the technology. Identical math, still trainable, still scalable, but running on embeddings we understand;{' '}
                  <span className="hl">
                    named axes: concepts a domain expert recognises, defends, and audits
                  </span>
                  . Same shape, same operations, but auditable by design.
                </>
              ) : (
                <>
                  We hebben de technologie heroverwogen. Identieke wiskunde, nog steeds trainbaar, nog steeds schaalbaar, maar nu werkend op embeddings die we begrijpen;{' '}
                  <span className="hl">
                    benoemde assen: concepten die een domeinexpert herkent, verdedigt en auditeert
                  </span>
                  . Zelfde vorm, zelfde operaties, maar van nature controleerbaar.
                </>
              )}
            </p>
          </Reveal>

          <div className="compare">
            <Reveal className="compare__col compare__col--alt">
              <p className="compare__head">{en ? "Today's AI" : 'De AI van vandaag'}</p>
              <p className="compare__title">{en ? 'A hidden embedding.' : 'Een verborgen embedding.'}</p>
              <p className="compare__body">
                {en
                  ? 'Inputs encode into a vector with hundreds or thousands of unnamed dimensions. The numbers carry meaning the model has learned, but no human can read them.'
                  : 'Inputs worden gecodeerd in een vector met honderden of duizenden niet-benoemde dimensies. De getallen dragen betekenis die het model heeft geleerd, maar geen mens kan ze lezen.'}
              </p>
              <pre className="compare__viz">
{`embedding = [
  0.412, -0.097,  1.038,
 -0.554,  0.281,  0.902,
  ... × 768
]`}
              </pre>
            </Reveal>
            <Reveal className="compare__col compare__col--dark">
              <p className="compare__head">{en ? 'Our AI' : 'Onze AI'}</p>
              <p className="compare__title">
                {en ? 'An interpretable embedding.' : 'Een interpreteerbare embedding.'}
              </p>
              <p className="compare__body">
                {en
                  ? 'Inputs encode into a vector of the same shape — but each dimension is a concept a domain expert verified: urgency, jurisdiction, structural risk, evidentiary weight. The same operations run on it. Every result traces back to the concepts that produced it.'
                  : 'Inputs worden gecodeerd in een vector van dezelfde vorm — maar elke dimensie is een concept dat een domeinexpert gekozen heeft: urgentie, jurisdictie, constructief risico, bewijskracht. Dezelfde operaties draaien erop. Elk resultaat is herleidbaar naar de concepten die het produceerden.'}
              </p>
              <pre className="compare__viz">
{`embedding = {
  urgency:           0.78,
  jurisdiction:      0.46,
  structural_risk:   0.62,
  evidentiary_weight:0.88,
}`}
              </pre>
            </Reveal>
          </div>
        </div>
      </section>

      {/* §3 — How it works */}
      <section className="section">
        <div className="container">
          <Reveal className="section-head">
            <span className="label">{en ? 'How it works' : 'Hoe het werkt'}</span>
            <h2 style={{ marginTop: 18 }}>
              {en ? 'Three pieces, working together.' : 'Drie onderdelen, die samenwerken.'}
            </h2>
            <p className="lede" style={{ marginTop: 24 }}>
              {en
                ? 'An interpretable substrate, with two components on either side: one to fill it with meaning, one to compute on it.'
                : 'Een interpreteerbare AI-laag, met aan weerszijden twee componenten: één die de laag met betekenis vult, één die erop rekent.'}
            </p>
          </Reveal>

          <div className="steps">
            <Reveal className="step-card">
              <p className="step-card__num">01 · Encoder</p>
              <h3 className="step-card__label">{en ? 'Reads input.' : 'Leest input.'}</h3>
              <p className="step-card__lead">
                {en ? 'A small language model populates the space.' : 'Een klein taalmodel vult de ruimte.'}
              </p>
              <p className="step-card__body">
                {en
                  ? "Text, records or sensor data flow in. The encoder does not define the space; it places each input on the named axes of the domain's ontology."
                  : 'Tekst, records of sensordata komen binnen. De encoder definieert de ruimte niet; hij plaatst elke input op de benoemde assen van de ontologie van het domein.'}
              </p>
              <div className="step-card__viz">
{`input.txt → encoder
encoder → { urgency: 0.78, … }`}
              </div>
            </Reveal>

            <Reveal className="step-card">
              <p className="step-card__num">{en ? '02 · Vector ontology' : '02 · Vector-ontologie'}</p>
              <h3 className="step-card__label">{en ? 'Holds meaning.' : 'Draagt betekenis.'}</h3>
              <p className="step-card__lead">
                {en
                  ? 'A finite-dimensional space with named axes.'
                  : 'Een eindig-dimensionale ruimte met benoemde assen.'}
              </p>
              <p className="step-card__body">
                {en
                  ? 'Each dimension is an interpretable concept a domain expert recognises and can defend. Entities, queries and answers all live in the same space.'
                  : 'Elke dimensie is een interpreteerbaar concept dat een domeinexpert herkent en kan verdedigen. Entiteiten, queries en antwoorden leven allemaal in dezelfde ruimte.'}
              </p>
              <div className="step-card__viz">
{`axes: [ urgency,
        jurisdiction,
        hazard_class, … ]`}
              </div>
            </Reveal>

            <Reveal className="step-card">
              <p className="step-card__num">
                {en ? '03 · Reasoning engines' : '03 · Redeneerengines'}
              </p>
              <h3 className="step-card__label">
                {en ? 'Computes answers.' : 'Berekent antwoorden.'}
              </h3>
              <p className="step-card__lead">
                {en ? 'Geometric operations on the space.' : 'Geometrische operaties op de ruimte.'}
              </p>
              <p className="step-card__body">
                {en
                  ? 'Search, classification, prediction, planning — all run as operations over named axes. Each output traces back to the concepts that produced it.'
                  : 'Zoeken, classificeren, voorspellen, plannen — alles draait als operatie over benoemde assen. Elke output is herleidbaar naar de concepten die hem produceerden.'}
              </p>
              <div className="step-card__viz">
{`query · k-nearest
            → top match
            ↳ explain axes`}
              </div>
            </Reveal>
          </div>

          <p style={{ textAlign: 'center', marginTop: 56 }}>
            <Link className="link" to="/platform">
              {en ? 'Read the architecture in detail' : 'Lees de architectuur in detail'}
            </Link>
          </p>
        </div>
      </section>

      {/* §4 — Problems it fits */}
      <section className="section">
        <div className="container">
          <Reveal className="section-head">
            <span className="label">{en ? 'Problems it fits' : 'Problemen waar het past'}</span>
            <h2 style={{ marginTop: 18 }}>
              {en
                ? 'Three categories of problems this technology was built for.'
                : 'Drie categorieën problemen waarvoor deze technologie is gebouwd.'}
            </h2>
            <p className="lede" style={{ marginTop: 24 }}>
              {en
                ? 'Interpretable AI by design unlocks domains where black-box machine learning cannot operate — not because of scale, but because of trust, language, complexity, or autonomy.'
                : 'Interpreteerbare AI van opzet ontsluit domeinen waar black-box machine learning niet kan opereren — niet vanwege schaal, maar vanwege vertrouwen, taal, complexiteit of autonomie.'}
            </p>
          </Reveal>

          <div className="asym-grid">
            <Reveal className="asym-tile asym-tile--feature">
              <div>
                <p className="asym-tile__label">
                  {en
                    ? '01 · Big-data decisions, not black boxes'
                    : '01 · Datagedreven besluiten, geen black box'}
                </p>
                <h3 className="asym-tile__title" style={{ marginTop: 12 }}>
                  {en
                    ? 'When decisions must draw on large volumes of data but cannot happen inside a black box.'
                    : 'Wanneer besluiten moeten steunen op grote hoeveelheden data, maar niet in een black box mogen plaatsvinden.'}
                </h3>
              </div>
              <p className="asym-tile__body">
                {en
                  ? 'Emergency services, operational command, clinical triage — wherever the cost of a wrong call is measured in lives, not clicks. The named-axis structure lets operators see exactly which data points drove each recommendation and why, while still processing everything a human alone would miss.'
                  : 'Hulpdiensten, operationeel commando, klinische triage — overal waar de kosten van een foute beslissing in levens worden gemeten, niet in klikken. De benoemde-assen-structuur laat operators precies zien welke datapunten elke aanbeveling hebben aangestuurd en waarom, terwijl alles wordt verwerkt wat een mens alleen zou missen.'}
              </p>
              <div className="asym-tile__viz">
                <pre
                  className="step-card__viz"
                  style={{ whiteSpace: 'pre', overflowX: 'auto', margin: 0 }}
                >
                  {en ? (
`data:     thousands of records, sensors, documents
process:  encode → named-axis reasoning → recommendation
output:   actionable insight + full trace per axis
verify:   operator inspects every dimension that mattered`
                  ) : (
`data:     duizenden records, sensoren, documenten
proces:   encodeer → benoemde-assen-redenering → aanbeveling
output:   handelingsgericht inzicht + volledige trace per as
verificatie: operator inspecteert elke dimensie die ertoe deed`
                  )}
                </pre>
                <p
                  style={{
                    margin: '14px 0 0',
                    fontFamily: 'var(--f-mono)',
                    fontSize: '0.75rem',
                    letterSpacing: '0.02em',
                    lineHeight: 1.6,
                    color: '#A8ADA8',
                  }}
                >
                  {en
                    ? 'Fits emergency services · ER triage · operational command.'
                    : 'Past bij hulpdiensten · SEH-triage · operationeel commando.'}
                </p>
              </div>
            </Reveal>

            <div className="asym-grid__col">
              <Reveal className="asym-tile">
                <p className="asym-tile__label">
                  {en
                    ? '02 · Plain-language access and keyword mismatch'
                    : '02 · Toegang in gewone taal en keyword-mismatch'}
                </p>
                <h3 className="asym-tile__title">
                  {en
                    ? 'When users ask in everyday language but answers live in specialist jargon.'
                    : 'Wanneer gebruikers in alledaagse taal vragen, maar antwoorden in vaktaal zijn geschreven.'}
                </h3>
                <p className="asym-tile__body">
                  {en
                    ? "Traditional search fails when the query and the answer don't share words. Our technology maps intent onto meaning, not keywords — bridging the gap between how citizens ask and how policy documents, legal codes, and specialist records are written."
                    : 'Traditioneel zoeken faalt wanneer de zoekvraag en het antwoord geen woorden delen. Onze technologie legt intentie op betekenis, niet op trefwoorden — en overbrugt zo de kloof tussen hoe burgers vragen en hoe beleidsdocumenten, wetteksten en specialistische dossiers zijn geschreven.'}
                </p>
              </Reveal>
              <Reveal className="asym-tile">
                <p className="asym-tile__label">
                  {en ? '03 · Complex solution spaces' : '03 · Complexe oplossingsruimtes'}
                </p>
                <h3 className="asym-tile__title">
                  {en
                    ? 'High-dimensional search where traditional systems fall short.'
                    : 'Hoogdimensionaal zoeken waar traditionele systemen tekortschieten.'}
                </h3>
                <p className="asym-tile__body">
                  {en
                    ? 'Material discovery, medication research, and other domains where the search space is vast and combinatorial. Named-axis spaces let researchers steer exploration along dimensions they understand — combining machine efficiency with expert intuition to navigate what brute-force methods cannot.'
                    : 'Materiaalontdekking, medicijnonderzoek en andere domeinen waar de zoekruimte enorm en combinatorisch is. Benoemde-assen-ruimtes laten onderzoekers de exploratie sturen langs dimensies die ze begrijpen — machinale efficiëntie gecombineerd met expertintuïtie om te navigeren waar brute-force-methoden falen.'}
                </p>
              </Reveal>
            </div>
          </div>

          <p style={{ textAlign: 'center', marginTop: 56 }}>
            <Link className="link" to="/applications">
              {en ? 'All patterns and live cases' : 'Alle patronen en live cases'}
            </Link>
          </p>
        </div>
      </section>

      {/* §5 — In production */}
      <section className="section">
        <div className="container">
          <Reveal className="section-head">
            <span className="label">{en ? 'In production' : 'In productie'}</span>
            <h2 style={{ marginTop: 18 }}>
              {en
                ? 'Two early pilots, both in Dutch public service.'
                : 'Twee vroege pilots, beide in de Nederlandse publieke sector.'}
            </h2>
            <p className="lede" style={{ marginTop: 24 }}>
              {en
                ? 'The first applications are in domains where wrong answers are expensive and decisions must be defensible.'
                : 'De eerste toepassingen zitten in domeinen waar foute antwoorden duur zijn en beslissingen verdedigbaar moeten zijn.'}
            </p>
          </Reveal>

          <Reveal as="article" className="case-feature">
            <div className="case-feature__image case-feature__image--olive case-feature__image--has-media">
              <div className="case-feature__media">
                <video autoPlay muted loop playsInline preload="metadata" src={HERO_CO_CREATION_VIDEO} />
                <div className="case-feature__media-overlay"></div>
              </div>
              <span className="case-feature__bubble">
                <span className="case-feature__bubble-dot"></span>
                <span className="case-feature__bubble-live">Live</span>
                <span>· MissionOS</span>
              </span>
              <div className="case-feature__logo">
                <img
                  src="/assets/website/logos/vnog.png"
                  alt="Veiligheidsregio Noord- en Oost-Gelderland (VNOG) — logo"
                />
              </div>
            </div>
            <div className="case-feature__body">
              <p className="case-feature__label">
                {en ? 'Case 01 · Public safety, Netherlands' : 'Case 01 · Publieke veiligheid, Nederland'}
              </p>
              <p className="case-feature__quote">
                {en
                  ? '"MissionOS can help me enormously by analyzing the data I don\'t have time for, sharpening my action perspective."'
                  : '"MissionOS kan me enorm helpen door de data te analyseren waar ik geen tijd voor heb, en zo mijn handelingsperspectief scherper te maken."'}
              </p>
              <p className="case-feature__author">
                {en ? 'Incident commander, VNOG' : 'Bevelvoerder, VNOG'}
              </p>
              <div className="case-feature__stats">
                <div>
                  <p className="case-feature__stat-num">&lt; 1 min</p>
                  <p className="case-feature__stat-label">
                    {en ? 'Incident to protocol' : 'Van incident naar protocol'}
                  </p>
                </div>
                <div>
                  <p className="case-feature__stat-num">150+</p>
                  <p className="case-feature__stat-label">
                    {en ? 'Simulations to date' : 'Simulaties tot dusver'}
                  </p>
                </div>
                <div>
                  <p className="case-feature__stat-num">100%</p>
                  <p className="case-feature__stat-label">
                    {en ? 'Traceable to source' : 'Herleidbaar tot de bron'}
                  </p>
                </div>
              </div>
              <p className="case-feature__summary">
                {en
                  ? 'MissionOS reads the situation along named axes — equipment, era, hazard class, jurisdiction — and routes incident commanders through scenario-trained protocols. Every recommendation is traceable to its source manual.'
                  : 'MissionOS leest de situatie langs benoemde assen — materieel, bouwjaar, gevarenklasse, jurisdictie — en leidt bevelvoerders door scenariogetrainde protocollen. Elke aanbeveling is herleidbaar naar het bronhandboek.'}
              </p>
              <Link className="link case-feature__link" to="/mission-os">
                {en ? 'Check our applications' : 'Bekijk onze toepassingen'}
              </Link>
            </div>
          </Reveal>

          <Reveal as="article" className="case-feature">
            <div className="case-feature__image case-feature__image--vmo case-feature__image--has-media">
              <div className="case-feature__media">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  src={CASE_VMO_DETAIL_VIDEO}
                  aria-label={
                    en
                      ? 'VraagMijnOverheid — close-up of the search report'
                      : 'VraagMijnOverheid — detailopname van het zoekrapport'
                  }
                />
                <div className="case-feature__media-overlay"></div>
              </div>
              <span className="case-feature__bubble">
                <span className="case-feature__bubble-dot"></span>
                <span className="case-feature__bubble-live">Pilot</span>
                <span>· JenV · 2026</span>
              </span>
              <div className="case-feature__logo case-feature__logo--color">
                <img
                  src="/assets/website/logos/jenv-wit.svg"
                  alt={
                    en
                      ? 'Ministry of Justice and Security'
                      : 'Ministerie van Justitie en Veiligheid'
                  }
                />
              </div>
            </div>
            <div className="case-feature__body">
              <p className="case-feature__label">
                {en
                  ? 'Case 02 · Pilot at JenV, Netherlands'
                  : 'Case 02 · Pilot bij JenV, Nederland'}
              </p>
              <p className="case-feature__quote">
                {en
                  ? '"It turns a citizen\'s question into a form we can both move forward with."'
                  : '"Het zet de vraag van een burger om in een vorm waar we beiden mee vooruit kunnen."'}
              </p>
              <p className="case-feature__author">
                {en
                  ? 'Policy officer, Woo intake team, Ministry of Justice and Security'
                  : 'Beleidsmedewerker, Woo-intaketeam, Ministerie van Justitie en Veiligheid'}
              </p>
              <div className="case-feature__stats">
                <div>
                  <p className="case-feature__stat-num">{en ? '66,000+' : '66.000+'}</p>
                  <p className="case-feature__stat-label">
                    {en ? 'Documents indexed' : 'Documenten geïndexeerd'}
                  </p>
                </div>
                <div>
                  <p className="case-feature__stat-num">100+</p>
                  <p className="case-feature__stat-label">
                    {en ? 'Users' : 'Gebruikers'}
                  </p>
                </div>
                <div>
                  <p className="case-feature__stat-num">20+</p>
                  <p className="case-feature__stat-label">
                    {en ? 'Request reports downloaded' : 'Verzoekrapporten gedownload'}
                  </p>
                </div>
              </div>
              <p className="case-feature__summary">
                {en
                  ? "Live pilot at the Ministry of Justice and Security. VraagMijnOverheid maps citizens' plain-language intent against published government documents along named axes the policy team can read and defend — where the answer is already public, the system shows it before a clerk gets involved, and fits alongside the existing Woo mailbox without complex integrations."
                  : 'Live pilot bij het ministerie van Justitie en Veiligheid. VraagMijnOverheid legt de intentie van burgers in gewone taal naast openbaar gepubliceerde overheidsdocumenten, langs benoemde assen die het beleidsteam kan lezen en verdedigen — waar het antwoord al openbaar is toont het systeem het voor er een ambtenaar bij hoeft, en het sluit aan op de bestaande Woo-mailbox zonder complexe integraties.'}
              </p>
              <Link className="link case-feature__link" to="/vmo">
                {en ? 'Check our applications' : 'Bekijk onze toepassingen'}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* §6 — Research */}
      <section className="section">
        <div className="container">
          <Reveal className="section-head">
            <span className="label">{en ? 'Research' : 'Onderzoek'}</span>
            <h2 style={{ marginTop: 18 }}>Papers</h2>
          </Reveal>

          <div className="paper-list">
            <Reveal className="paper-list__item">
              <span className="paper-list__year">2026</span>
              <div>
                <p className="paper-list__title">
                  {en
                    ? 'Domain-Property Alignment in Single-Vector Retrieval'
                    : 'Domein-eigenschap-alignment in single-vector retrieval'}
                </p>
                <p className="paper-list__desc">
                  {en
                    ? 'A constructive counter-example to LIMIT. On a benchmark designed to make dense retrieval fail, a one-hot property basis at d=1848 achieves recall@2 = 1.000 — against 0.000–0.019 for SOTA dense retrievers. The bottleneck is basis alignment, not dimension.'
                    : 'Een constructief tegenvoorbeeld op LIMIT. Op een benchmark ontworpen om dense retrieval te laten falen, haalt een one-hot eigenschap-basis op d=1848 een recall@2 van 1,000 — tegenover 0,000–0,019 voor SOTA dense retrievers. De bottleneck is basis-uitlijning, niet dimensie.'}
                </p>
              </div>
              <div className="paper-list__actions">
                <Link className="paper-list__link" to="/research/limit">
                  {en ? 'Plain language' : 'In gewone taal'}
                </Link>
                <a
                  className="paper-list__link paper-list__link--quiet"
                  href="/limit.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Paper
                </a>
              </div>
            </Reveal>
            <Reveal className="paper-list__item">
              <span className="paper-list__year">2025</span>
              <div>
                <p className="paper-list__title">
                  Vector Ontologies as an LLM World View Extraction Method
                </p>
                <p className="paper-list__desc">
                  {en
                    ? 'First empirical validation. Across 47 differently-phrased queries about the same musical genre, an LLM placed answers in 12.5 unique cells out of 1.7 million — stable, reproducible, and reviewable along named axes.'
                    : 'Eerste empirische validatie. Over 47 anders geformuleerde queries over hetzelfde muziekgenre plaatste een LLM antwoorden in 12,5 unieke cellen op 1,7 miljoen — stabiel, reproduceerbaar en controleerbaar langs benoemde assen.'}
                </p>
              </div>
              <div className="paper-list__actions">
                <Link className="paper-list__link" to="/research/worldview">
                  {en ? 'Plain language' : 'In gewone taal'}
                </Link>
                <a
                  className="paper-list__link paper-list__link--quiet"
                  href="https://arxiv.org/abs/2506.13252"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Paper
                </a>
              </div>
            </Reveal>
            <Reveal className="paper-list__item">
              <span className="paper-list__year">2025</span>
              <div>
                <p className="paper-list__title">
                  To Be or Not To Be: Vector ontologies as a truly formal ontological framework
                </p>
                <p className="paper-list__desc">
                  {en
                    ? 'A theoretical case for vector spaces as the genuine home of formal ontology — and why named-axis substrates can carry both human and machine meaning.'
                    : 'Een theoretische onderbouwing voor vectorruimtes als de echte plek voor formele ontologie — en waarom AI-lagen met benoemde assen zowel menselijke als machinale betekenis kunnen dragen.'}
                </p>
              </div>
              <div className="paper-list__actions">
                <Link className="paper-list__link" to="/research/vector-ontologies">
                  {en ? 'Plain language' : 'In gewone taal'}
                </Link>
                <a
                  className="paper-list__link paper-list__link--quiet"
                  href="https://arxiv.org/abs/2505.14940"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Paper
                </a>
              </div>
            </Reveal>
          </div>

          <p style={{ textAlign: 'center', marginTop: 56 }}>
            <Link className="link" to="/research">
              {en
                ? 'All research, talks and plain-language versions'
                : 'Al het onderzoek, talks en versies in gewone taal'}
            </Link>
          </p>
        </div>
      </section>

      {/* §7 — Team */}
      <section className="section">
        <div className="container">
          <Reveal className="section-head">
            <span className="label">{en ? 'Built by' : 'Gebouwd door'}</span>
            <h2 style={{ marginTop: 18 }}>
              {en
                ? 'A small international team, based in the Netherlands.'
                : 'Een klein, internationaal team, gevestigd in Nederland.'}
            </h2>
          </Reveal>

          <div className="team">
            <Reveal className="team__card">
              <img
                className="team__photo"
                src="/assets/website/photos/kaspar.webp"
                alt="Kaspar Rothenfusser"
              />
              <p className="team__name">Kaspar Rothenfusser</p>
              <p className="team__role">Chief Science Officer</p>
              <p className="team__line">
                {en
                  ? 'Sets the research direction — what we study, what we publish, and how that feeds the product.'
                  : 'Bepaalt de onderzoeksrichting: wat we bestuderen, wat we publiceren, en hoe dat het product voedt.'}
              </p>
            </Reveal>
            <Reveal className="team__card">
              <img className="team__photo" src="/assets/website/photos/bekk.webp" alt="Bekk Blando" />
              <p className="team__name">Bekk Blando</p>
              <p className="team__role">Chief Technology Officer</p>
              <p className="team__line">
                {en
                  ? 'Builds the platform that runs in production. Owns architecture and the API.'
                  : 'Bouwt het platform dat in productie draait. Verantwoordelijk voor architectuur en de API.'}
              </p>
            </Reveal>
            <Reveal className="team__card">
              <img
                className="team__photo"
                src="/assets/website/photos/rens.webp"
                alt="Rens Dommerholt"
              />
              <p className="team__name">Rens Dommerholt</p>
              <p className="team__role">Chief Executive Officer</p>
              <p className="team__line">
                {en
                  ? 'Runs the company: sales, partnerships, compliance, and delivery.'
                  : 'Runt het bedrijf: sales, partnerships, compliance en oplevering.'}
              </p>
            </Reveal>
          </div>

          <p style={{ textAlign: 'center', marginTop: 56 }}>
            <Link className="link" to="/about">
              {en ? 'More about us' : 'Meer over ons'}
            </Link>
          </p>
        </div>
      </section>
    </WebsiteLayout>
  );
};

export default NLLanding;
